import {
  SwitchingBlockRestrictionTypeEnum,
  SwitchingBlockRestrictionWithBlocks,
} from '@gridcon-energy/schaltungstool-backend'
import type {
  SwitchingBlockRead,
  SwitchingBlockWithSwitchings,
  SwitchingRead,
} from '@gridcon-energy/schaltungstool-backend'
import dayjs from 'dayjs/esm'
import { computed, inject, provide, reactive, ref } from 'vue'
import { asEndOfDay, DateRangeRequired } from '~/lib/gantt'
import type {
  SwitchingBlockMinMaxRange,
  SwitchingBlockForbiddenViolation,
  SwitchingBlockPreviousViolation,
  SwitchingBlockRestrictionRelations,
  SwitchingBlocksMinMaxRange,
} from '~/lib/gantt'
import { injectBackendClient } from '~/api/backendClients'
import { SBlockRestrictionsRepositorySymbol } from '~/lib/symbols'
import { defineStore } from 'pinia'
import type { NonEmptyArray } from '~/types/helpers'
import { isNotEmpty } from '~/helper'

/**
 * Finds direkt connections of Switching Blocks and (infinite) indirect connections via parallel restrictions.
 *
 * If a switching block id is found in multiple restrictions, all switching blocks of those restrictions form a union.
 * In the following example the restriction "C" connects all switching block ids of "A" and "B", which means
 * that "1" is connected to all member of "A", "B", "C" as well as every other switching block id.
 * e.G. A:(1,2), B:(3,4), C:(2,4)
 *
 * @param directSwitchingBlockConnections
 */
function generateRestrictionParallelRelations(directSwitchingBlockConnections: {
  [key: number]: Set<number>
}): SwitchingBlockRestrictionRelations {
  // Perform depth-first search (DFS) traversal for each node
  // For visuell presentation see https://www.geeksforgeeks.org/depth-first-search-or-dfs-for-a-graph/
  for (const switchingBlockId in directSwitchingBlockConnections) {
    // stack defines connected nodes to visit
    let stack = [...directSwitchingBlockConnections[switchingBlockId]]
    const visited = new Set([parseInt(switchingBlockId)])
    while (stack.length) {
      const current = stack.pop()
      // skip visited
      if (current === undefined || visited.has(current)) {
        continue
      }
      visited.add(current)

      // add current node as relation
      directSwitchingBlockConnections[switchingBlockId].add(current)

      // add connected nodes of current node onto stack
      stack.push(...directSwitchingBlockConnections[current])
      stack = Array.from(new Set(stack))
    }
  }

  const switchingBlocksParallel: SwitchingBlockRestrictionRelations = {}
  for (const id in directSwitchingBlockConnections) {
    switchingBlocksParallel[id] = Array.from(directSwitchingBlockConnections[id])
  }
  return switchingBlocksParallel
}

function getSwitchingsOfSwitchingBlockIds(
  switchingBlocks: SwitchingBlockWithSwitchings[],
  ...switchingBlockIds: number[]
) {
  const switchings: SwitchingRead[] = []
  const minMaxRanges: SwitchingBlockMinMaxRange[] = []

  for (const blockId of switchingBlockIds) {
    const block = switchingBlocks.find((block) => block.id === blockId)
    if (block && isNotEmpty(block.switchings)) {
      switchings.push(...block.switchings)
      minMaxRanges.push({
        switchingBlockId: blockId,
        dateRange: getMinMaxRange(block.switchings),
      })
    } else {
      console.warn(`No switchings found for switching block with id ${blockId}`)
    }
  }

  return { switchings, minMaxRanges }
}

/**
 * Checks if a given set of switchings (source) are all after set of other switchings (target)
 * and places the ranges by which the target switching is over the source switching into a container
 * @param switchingsSource
 * @param switchingsTarget
 */
function checkTimeRangeSwitchingBlockRestrictionPrevious(
  switchingsSource: SwitchingRead[],
  switchingsTarget: SwitchingRead[],
) {
  const restrictionsViolatedPrevious: SwitchingBlockPreviousViolation[] = []
  for (const switchingSource of switchingsSource) {
    for (const switchingTarget of switchingsTarget) {
      if (dayjs(switchingTarget.end).isAfter(dayjs(switchingSource.start))) {
        restrictionsViolatedPrevious.push({
          currentBarId: switchingSource.id,
          currentSwitchingBlockId: switchingSource.switching_block_id,
          currentBarStart: dayjs(switchingSource.start),
          previousSwitchingBlockId: switchingTarget.switching_block_id,
          previousBarId: switchingTarget.id,
          previousBarEnd: asEndOfDay(switchingTarget.end),
        })
      }
    }
  }
  return restrictionsViolatedPrevious
}

/**
 * Checks if a given set of switchings (source) has overlapping dates with a set of other switchings (target)
 * and places the ranges into a container
 * @param switchingsSource
 * @param switchingsTarget
 * @param forbiddenRanges
 */
function checkTimeRangeSwitchingBlockParallelForbidden(
  switchingsSource: SwitchingRead[],
  switchingsTarget: SwitchingRead[],
  forbiddenRanges: SwitchingBlocksMinMaxRange,
) {
  const restrictionsViolatedParallelForbidden: SwitchingBlockForbiddenViolation[] = []
  for (const switching of switchingsTarget) {
    for (const forbiddenRage of forbiddenRanges) {
      // check whether two date ranges overlap
      // https://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap

      const overlap =
        dayjs(switching.end).isSameOrAfter(dayjs(forbiddenRage.dateRange.start)) &&
        dayjs(switching.start).isSameOrBefore(dayjs(forbiddenRage.dateRange.end))

      if (overlap) {
        const overlapRange: SwitchingBlockForbiddenViolation = {
          switchingBlockId: switching.switching_block_id,
          barId: switching.id,
          nonParallelSwitchingBlockId: forbiddenRage.switchingBlockId,
          nonParallelBarIds: switchingsSource.map((switching) => switching.id),
          start: dayjs(
            dayjs(forbiddenRage.dateRange.start).isBefore(switching.start)
              ? switching.start
              : forbiddenRage.dateRange.start,
          ),
          end: asEndOfDay(
            dayjs(forbiddenRage.dateRange.end).isAfter(switching.end) ? switching.end : forbiddenRage.dateRange.end,
          ),
        }
        restrictionsViolatedParallelForbidden.push(overlapRange)
      }
    }
  }
  return restrictionsViolatedParallelForbidden
}

/**
 * Returns start date for earliest switching and end date of latest switching
 *
 * Because we use reduce() we need a non empty array
 */
function getMinMaxRange(switchings: NonEmptyArray<SwitchingRead>): DateRangeRequired {
  const switchingStartDates: string[] = []
  const switchingEndDates: string[] = []

  // get start/end dates of switchings
  for (const switching of switchings) {
    switchingStartDates.push(switching.start)
    switchingEndDates.push(switching.end)
  }

  // get the earliest start date of switching block
  const minStartDateSwitchingBlock = switchingStartDates.reduce((accumulator, currentValue) =>
    dayjs(accumulator).isBefore(currentValue) ? accumulator : currentValue,
  )

  // get the latest end date of switching block
  const maxEndDateSwitchingBlock = switchingEndDates.reduce((accumulator, currentValue) =>
    dayjs(accumulator).isAfter(currentValue) ? accumulator : currentValue,
  )

  return {
    start: minStartDateSwitchingBlock,
    end: maxEndDateSwitchingBlock,
  }
}
export type RestrictionsMap = {
  previous: SwitchingBlockRestrictionRelations
  parallelForbidden: SwitchingBlockRestrictionRelations
  parallel: SwitchingBlockRestrictionRelations // value also contains source SwitchingBlock
}

type SBlockRestrictionsViolatedMap = {
  previous: SwitchingBlockPreviousViolation[]
  parallelForbidden: SwitchingBlockForbiddenViolation[]
}

/**
 * Store
 */
const switchingBlockRestrictionsStore = defineStore('switching-block-restrictions', () => {
  const switchingBlockRestrictions = ref<SwitchingBlockRestrictionWithBlocks[]>([])
  const restrictions = reactive<RestrictionsMap>({
    previous: {},
    parallelForbidden: {},
    parallel: {},
  })
  const restrictionsViolated = reactive<SBlockRestrictionsViolatedMap>({
    previous: [],
    parallelForbidden: [],
  })

  const restrictionsViolatedSwitchingIds = computed(() => {
    const switchingIds: number[] = []
    // previous
    for (const violation of restrictionsViolated.previous) {
      switchingIds.push(violation.currentBarId, violation.previousBarId)
    }

    // parallel forbidden
    for (const violation of restrictionsViolated.parallelForbidden) {
      switchingIds.push(violation.barId)
      if (violation.nonParallelBarIds) {
        switchingIds.push(...violation.nonParallelBarIds)
      }
    }
    return switchingIds
  })
  return {
    restrictions,
    switchingBlockRestrictions: switchingBlockRestrictions,
    restrictionsViolated: restrictionsViolated,
    restrictionsViolatedSwitchingIds: restrictionsViolatedSwitchingIds,
  }
})

export type SBlockRestrictionsRepository = ReturnType<typeof createSBlockRestrictionsRepository>
export default function createSBlockRestrictionsRepository(activityId: number) {
  const { switchingBlockRestrictions, restrictions, restrictionsViolated, restrictionsViolatedSwitchingIds } =
    switchingBlockRestrictionsStore()
  const backend = injectBackendClient()

  /**
   * Fetches and maps the given array of restrictions to their respective object
   */
  async function resolveRestrictions() {
    const response = await backend.switchingBlockRestrictions.switchingBlocksWithRestrictionsWithSB(activityId)
    switchingBlockRestrictions.splice(0, switchingBlockRestrictions.length, ...response.data)

    // SwitchingBlockId -> Related SwitchingBlockIds
    const directSwitchingBlockConnections: { [key: number]: Set<number> } = {}
    for (const restriction of response.data) {
      // Parallel
      if (restriction.type === SwitchingBlockRestrictionTypeEnum.Parallel) {
        const sourceSwitchingBlockId = restriction.source_switching_block_id
        if (!directSwitchingBlockConnections[sourceSwitchingBlockId]) {
          directSwitchingBlockConnections[sourceSwitchingBlockId] = new Set()
        }

        for (const relatedBlock of restriction.switching_blocks) {
          const relatedBlockId = relatedBlock.id
          if (!directSwitchingBlockConnections[relatedBlockId]) {
            directSwitchingBlockConnections[relatedBlockId] = new Set()
          }

          // Set direct connections
          directSwitchingBlockConnections[sourceSwitchingBlockId].add(sourceSwitchingBlockId)
          directSwitchingBlockConnections[sourceSwitchingBlockId].add(relatedBlockId)
          directSwitchingBlockConnections[relatedBlockId].add(sourceSwitchingBlockId)
          directSwitchingBlockConnections[relatedBlockId].add(relatedBlockId)
        }
        continue
      }

      // Parallel Forbidden && previous
      const container =
        restriction.type === SwitchingBlockRestrictionTypeEnum.ParallelForbidden
          ? restrictions.parallelForbidden
          : restrictions.previous
      container[restriction.source_switching_block_id] = restriction.switching_blocks
        .map((switchingBlock: SwitchingBlockRead) => switchingBlock.id)
        .filter((switchingBlockId: number) => switchingBlockId !== restriction.source_switching_block_id)
    }

    restrictions.parallel = generateRestrictionParallelRelations(directSwitchingBlockConnections)
  }

  /**
   * Iterates over fetched SwitchingBlocks and SwitchingBlockRestrictions to generate
   * objects necessary to render the Restrictions in the gantt-chart.
   */
  async function resolveRestrictionViolations() {
    const responseBlocks = await backend.switchingBlocks.switchingBlockWithSwitchingsAll(activityId)

    // helper function
    function getSwitchingsData(sourceBlockId: number, switchingBlockRelations: SwitchingBlockRestrictionRelations) {
      // Job1: get switchings of source switching block
      const { switchings: switchingsSource, minMaxRanges: sourceSwitchingBlocksMinMaxRanges } =
        getSwitchingsOfSwitchingBlockIds(responseBlocks.data, sourceBlockId)

      // Job2: get switchings of target switching blocks
      const { switchings: switchingsTarget } = getSwitchingsOfSwitchingBlockIds(
        responseBlocks.data,
        ...switchingBlockRelations[sourceBlockId],
      )
      return { switchingsSource, switchingsTarget, sourceSwitchingBlocksMinMaxRanges }
    }

    // parallelForbidden
    const parallelForbidden: SwitchingBlockForbiddenViolation[] = []
    for (const switchingBlock in restrictions.parallelForbidden) {
      const sourceSwitchingBlockId = parseInt(switchingBlock)
      const switchingsData = getSwitchingsData(sourceSwitchingBlockId, restrictions.parallelForbidden)

      parallelForbidden.push(
        ...checkTimeRangeSwitchingBlockParallelForbidden(
          switchingsData.switchingsSource,
          switchingsData.switchingsTarget,
          switchingsData.sourceSwitchingBlocksMinMaxRanges,
        ),
      )
    }

    // previous
    const previous: SwitchingBlockPreviousViolation[] = []
    for (const switchingBlock in restrictions.previous) {
      const sourceSwitchingBlockId = parseInt(switchingBlock)
      const switchingsData = getSwitchingsData(sourceSwitchingBlockId, restrictions.previous)
      previous.push(
        ...checkTimeRangeSwitchingBlockRestrictionPrevious(
          switchingsData.switchingsSource,
          switchingsData.switchingsTarget,
        ),
      )
    }
    restrictionsViolated.previous = previous
    restrictionsViolated.parallelForbidden = parallelForbidden
  }

  function getRelatedSwitchingBlocks(switching: SwitchingRead): number[] {
    return restrictions.parallel[switching.switching_block_id] ?? [switching.switching_block_id]
  }

  const data = {
    resolveRestrictions: resolveRestrictions,
    restrictions: restrictions,
    getRelatedSwitchingBlocks: getRelatedSwitchingBlocks,

    resolveRestrictionViolations: resolveRestrictionViolations,
    restrictionsViolated: restrictionsViolated,
    restrictionsViolatedSwitchingIds: restrictionsViolatedSwitchingIds,
  }
  provide(SBlockRestrictionsRepositorySymbol, data)
  return data
}

export function injectSBlockRestrictionsRepository() {
  const repo = inject(SBlockRestrictionsRepositorySymbol)
  if (!repo) {
    throw new Error('Missing injection key')
  }
  return repo
}
